import React, { useMemo, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { PaginatedEntityTable, ShareButton } from 'components/common';
import type { Sort } from 'stores/PaginationTypes';
import { ButtonToolbar } from 'components/bootstrap';
import type { Collection, CollectionEntity } from 'collections/types';
import { fetchPaginatedCollectionEntities, keyFn } from 'collections/hooks/useCollectionEntities';
import EntityShareModal from 'components/permissions/EntityShareModal';
import { COLLECTIONS_QUERY_KEY } from 'collections/hooks/useCollections';

import CollectionEntityActions from './CollectionEntityActions';
import customColumnRenderers from './customColumnRenderers';
import BulkActions from './BulkActions';

export const DEFAULT_LAYOUT = {
  entityTableId: 'collection_entities',
  defaultPageSize: 20,
  defaultSort: { attributeId: 'name', direction: 'asc' } as Sort,
  defaultDisplayedAttributes: ['name', 'type', 'owners'],
};

const COLUMNS_ORDER = ['name', 'type', 'owners'];

type Props = {
  collectionId: Collection['id'];
  collectionTitle: Collection['title'];
};

const CollectionEntitiesList = ({ collectionId, collectionTitle }: Props) => {
  const queryClient = useQueryClient();
  const [showCollectionContentShareModal, setShowCollectionContentShareModal] = useState<boolean>(false);
  const toggleCollectionContentShareModal = () => setShowCollectionContentShareModal(!showCollectionContentShareModal);
  const [selectedEntities, setSelectedEntities] = useState<Array<CollectionEntity>>([]);

  const onBulkSelect = (selected: Array<string>, data: Array<CollectionEntity>) => {
    setSelectedEntities(data.filter((entity) => selected.includes(entity.id)));
  };

  const bulkSelection = useMemo(
    () => ({
      onChangeSelection: onBulkSelect,
      actions: <BulkActions selectedEntities={selectedEntities} collectionId={collectionId} />,
    }),
    [collectionId, selectedEntities],
  );

  const refetchCollection = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [COLLECTIONS_QUERY_KEY, collectionId],
      }),
    [queryClient, collectionId],
  );

  const handleShareClose = () => {
    refetchCollection();

    toggleCollectionContentShareModal();
  };
  const collectionEntitiesActions = useCallback(
    ({ id, name, type }: CollectionEntity) => (
      <CollectionEntityActions type={type} collectionId={collectionId} id={id} name={name} />
    ),
    [collectionId],
  );

  return (
    <>
      <PaginatedEntityTable<CollectionEntity>
        humanName="Collection Content"
        columnsOrder={COLUMNS_ORDER}
        entityActions={collectionEntitiesActions}
        tableLayout={DEFAULT_LAYOUT}
        fetchEntities={(searchParams) => fetchPaginatedCollectionEntities(collectionId, searchParams)}
        keyFn={(searchParams) => keyFn(collectionId, searchParams)}
        columnRenderers={customColumnRenderers}
        entityAttributesAreCamelCase={false}
        searchPlaceholder="Search for content"
        bulkSelection={bulkSelection}
        topRightCol={
          <ButtonToolbar>
            <ShareButton
              title="Share Content"
              entityId={collectionId}
              entityType="collection_entities"
              onClick={toggleCollectionContentShareModal}
            />
            {/* TODO: add back after star permissions are handled.
              <IfPermitted permissions={`collection_entities:edit:${collectionId}`}>
                <Button bsStyle="primary" onClick={onShowAddModal}>
                  <Icon name="add" /> Add Content
                </Button>
              </IfPermitted>
            */}
          </ButtonToolbar>
        }
      />
      {showCollectionContentShareModal && (
        <EntityShareModal
          entityId={collectionId}
          entityType="collection_entities"
          entityTypeTitle="Collection Content"
          entityTitle={collectionTitle}
          description="Search for a User or Team to add as collaborator on this collection's content."
          onClose={handleShareClose}
        />
      )}
    </>
  );
};

export default CollectionEntitiesList;
